import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Bell, Check, Info, AlertTriangle, ShieldAlert } from "lucide-react";

export default function Notifications() {
  const qc = useQueryClient();

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get("/notifications").then(r => r.data),
  });

  const readMutation = useMutation({
    mutationFn: (id) => api.patch(`/notifications/${id}/read`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["notifications-unread"] });
    },
  });

  return (
    <div className="p-4 lg:p-8 max-w-3xl mx-auto">
      <header className="mb-6">
        <h1 className="font-heading text-3xl font-extrabold text-navy">Notificaciones</h1>
        <p className="text-sm text-muted-foreground mt-1">Alertas y avisos del sistema de seguridad.</p>
      </header>

      {isLoading ? (
        <p className="text-muted-foreground">Cargando...</p>
      ) : notifications.length === 0 ? (
        <div className="bg-white border border-border p-12 text-center">
          <Bell size={48} className="mx-auto text-muted-foreground mb-4 opacity-20" />
          <p className="text-navy font-bold">No hay notificaciones</p>
          <p className="text-sm text-muted-foreground">Todo está al día en el patio.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((n) => (
            <div
              key={n.id}
              className={`bg-white border p-4 flex gap-4 transition-all ${
                n.read ? "border-border opacity-60" : "border-navy bg-navy/5 shadow-sm"
              }`}
            >
              <div className={`w-10 h-10 shrink-0 flex items-center justify-center rounded-full ${
                n.type === 'critical' ? 'bg-critical/10 text-critical' :
                n.type === 'warn' ? 'bg-warn/10 text-warn' : 'bg-navy/10 text-navy'
              }`}>
                {n.type === 'critical' ? <ShieldAlert size={20} /> :
                 n.type === 'warn' ? <AlertTriangle size={20} /> : <Info size={20} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className={`text-sm font-bold ${n.read ? 'text-navy/70' : 'text-navy'}`}>{n.title}</h3>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                    {new Date(n.created_at).toLocaleString('es-MX', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{n.body}</p>
                {!n.read && (
                  <button
                    onClick={() => readMutation.mutate(n.id)}
                    className="mt-3 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-navy hover:underline"
                  >
                    <Check size={12} /> Marcar como leída
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
